/**
 * DataTable — DS Organism
 * Tri, pagination intégrée, actions par ligne, skeleton loading
 */

import { useState } from 'react';
import { Pagination } from '../../molecules';
import { Spinner } from '../../atoms';
import styles from './DataTable.module.css';

export interface Column<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  width?: string;
}

interface DataTableProps<T extends Record<string, unknown>> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  rowKey: keyof T;
  actions?: (row: T) => React.ReactNode;
  pageSize?: number;
  className?: string;
}

export function DataTable<T extends Record<string, unknown>>({
  columns, data, loading = false, rowKey, actions, pageSize = 10, className = '',
}: DataTableProps<T>) {
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = sortKey
    ? [...data].sort((a, b) => {
        const av = a[sortKey]; const bv = b[sortKey];
        const cmp = String(av ?? '').localeCompare(String(bv ?? ''));
        return sortAsc ? cmp : -cmp;
      })
    : data;

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: string) => {
    if (sortKey === key) setSortAsc(p => !p);
    else { setSortKey(key); setSortAsc(true); }
    setPage(1);
  };

  if (loading) return (
    <div className={styles.loadingWrapper}><Spinner size="lg" label="Chargement..." /></div>
  );

  return (
    <div className={`${styles.wrapper} ${className}`}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map(col => (
                <th
                  key={String(col.key)}
                  className={`${styles.th} ${col.sortable ? styles.sortable : ''}`}
                  style={{ width: col.width }}
                  onClick={() => col.sortable && handleSort(String(col.key))}
                >
                  {col.label}
                  {col.sortable && (
                    <i className={`bi ${sortKey === col.key ? (sortAsc ? 'bi-sort-up' : 'bi-sort-down') : 'bi-sort'} ms-1`} style={{ fontSize: '0.75rem' }} />
                  )}
                </th>
              ))}
              {actions && <th className={styles.th} style={{ width: '80px' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 && (
              <tr><td colSpan={columns.length + (actions ? 1 : 0)} className={styles.empty}>Aucun résultat</td></tr>
            )}
            {paginated.map(row => (
              <tr key={String(row[rowKey])} className={styles.row}>
                {columns.map(col => (
                  <td key={String(col.key)} className={styles.td}>
                    {col.render ? col.render(row) : String(row[String(col.key)] ?? '')}
                  </td>
                ))}
                {actions && <td className={styles.td}>{actions(row)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationRow}>
          <span className={styles.count}>{sorted.length} résultats</span>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
